import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { studentService } from '../services';
import { ArtworkObjectImage } from '../components/artwork/ArtworkObjectImage';
import { LoadingSpinner, ErrorBoundary, InlineError, ServiceOutageIndicator, RetryableError } from '../components/ui';
import { GalleryGrid } from '../components/layout';
import {
  galleryColors,
  gallerySpacing,
  galleryBorderRadius,
  galleryShadows,
  galleryTransitions
} from '../styles/galleryHelpers';

const PageContainer = styled.div<{ $galleryMode?: boolean }>`
  max-width: ${({ $galleryMode }) => $galleryMode ? '1400px' : '1200px'};
  margin: 0 auto;
  padding: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `${gallerySpacing.section({ theme })} ${gallerySpacing.margin({ theme })}`
      : `${theme.spacing.xl} ${theme.spacing.lg}`};
  
  ${({ $galleryMode, theme }) => $galleryMode && `
    background-color: ${galleryColors.white({ theme })};
    min-height: 100vh;
    padding-top: ${gallerySpacing.lg({ theme })};
    padding-bottom: ${gallerySpacing.xl({ theme })};
    
    @media (min-width: ${theme.breakpoints.tablet}) {
      padding: ${gallerySpacing.lg({ theme })} ${gallerySpacing.gutter({ theme })} ${gallerySpacing.xl({ theme })};
    }
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `${gallerySpacing.xs({ theme })} ${gallerySpacing.sm({ theme })} ${gallerySpacing.md({ theme })}`
      : `${theme.spacing.lg} ${theme.spacing.md}`};
  }
`;

const Header = styled.div<{ $galleryMode?: boolean }>`
  text-align: center;
  margin-bottom: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.sm({ theme }) : theme.spacing['4xl']};
  
  ${({ $galleryMode }) => $galleryMode && `
    position: relative;
    padding: 0;
  `}
`;

const Title = styled.h1<{ $galleryMode?: boolean }>`
  font-family: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontFamily.gallery.display : theme.typography.fontFamily.heading};
  font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.hero : theme.typography.fontSize['4xl']};
  font-weight: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontWeight.gallery.light : theme.typography.fontWeight.bold};
  color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.charcoal({ theme }) : theme.colors.text};
  margin-bottom: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.tiny({ theme }) : theme.spacing.md};
  line-height: ${({ theme }) => theme.typography.lineHeight?.tight || 1.1};
  letter-spacing: ${({ $galleryMode }) => $galleryMode ? '-0.01em' : 'normal'};

  ${({ $galleryMode }) => $galleryMode && `line-height: 1;`}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.display : theme.typography.fontSize['3xl']};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.section : theme.typography.fontSize['2xl']};
  }
`;

const Subtitle = styled.p<{ $galleryMode?: boolean }>`
  font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.caption : theme.typography.fontSize.lg};
  font-family: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontFamily.gallery.body : 'inherit'};
  font-weight: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontWeight.gallery.light : 'normal'};
  color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.neutralDark({ theme }) : theme.colors.gray.dark};
  max-width: ${({ $galleryMode }) => $galleryMode ? '700px' : '600px'};
  margin: 0 auto;
  line-height: ${({ theme, $galleryMode }) => $galleryMode ? 1.4 : (theme.typography.lineHeight?.relaxed || 1.6)};
  letter-spacing: ${({ $galleryMode }) => $galleryMode ? '0.005em' : 'normal'};
`;

const StudentCard = styled(Link) <{ $galleryMode?: boolean }>`
  display: block;
  min-width: 0;
  background: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.white({ theme }) : theme.colors.white};
  border-radius: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryBorderRadius.comfortable({ theme }) : theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryShadows.artworkSubtle({ theme }) : theme.shadows.md};
  transition: all ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryTransitions.hover({ theme }) : theme.transitions.normal};
  text-decoration: none;
  color: inherit;
  
  ${({ $galleryMode, theme }) => $galleryMode && `
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.05) 50%,
        rgba(0, 0, 0, 0.02) 100%
      );
      pointer-events: none;
      z-index: 1;
      border-radius: inherit;
      opacity: 0;
      transition: opacity ${galleryTransitions.hover({ theme })};
    }
    
    &:hover::before {
      opacity: 1;
    }
  `}

  &:hover {
    transform: ${({ $galleryMode }) =>
    $galleryMode ? 'translateY(-6px) scale(1.01)' : 'translateY(-4px)'};
    box-shadow: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryShadows.artworkFrame({ theme }) : theme.shadows.lg};
  }
`;

const StudentHeader = styled.div<{ $galleryMode?: boolean }>`
  min-width: 0;
  padding: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.sm({ theme }) : theme.spacing.lg};
  background: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `linear-gradient(135deg, ${galleryColors.neutralLightest({ theme })}, ${galleryColors.softWhite({ theme })})`
      : `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`};
  border-bottom: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `1px solid ${galleryColors.neutralLight({ theme })}`
      : `1px solid ${theme.colors.gray.light}`};
  
  ${({ $galleryMode }) => $galleryMode && `
    position: relative;
    z-index: 2;
  `}
`;

const StudentName = styled.h3<{ $galleryMode?: boolean }>`
  font-family: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontFamily.gallery.heading : theme.typography.fontFamily.heading};
  font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.title : theme.typography.fontSize.xl};
  font-weight: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontWeight.gallery.medium : theme.typography.fontWeight.semibold};
  color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.charcoal({ theme }) : theme.colors.text};
  margin-bottom: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.micro({ theme }) : theme.spacing.sm};
  margin-top: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight?.snug || 1.25};
  letter-spacing: ${({ $galleryMode }) => $galleryMode ? '0.005em' : 'normal'};
  margin: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ArtworkCount = styled.p<{ $galleryMode?: boolean }>`
  color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.neutralDark({ theme }) : theme.colors.gray.dark};
  font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.caption : theme.typography.fontSize.sm};
  font-family: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontFamily.gallery.caption : 'inherit'};
  font-weight: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontWeight.gallery.light : 'normal'};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  letter-spacing: ${({ $galleryMode }) => $galleryMode ? '0.005em' : 'normal'};
`;

const PreviewFrame = styled.div<{ $galleryMode?: boolean }>`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gallery.mat};
`;

const PreviewContent = styled.div<{ $galleryMode?: boolean }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  padding: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.tiny({ theme }) : theme.spacing.md};
`;

const PreviewImage = styled(ArtworkObjectImage)`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;
  object-fit: contain;
  object-position: center;
`;

const PreviewFallback = styled.span`
  color: ${({ theme }) => theme.colors.gallery.text.muted};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['4xl']} ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const EmptyStateIcon = styled.div`
  width: 3rem;
  height: 3rem;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  border: 2px solid ${({ theme }) => theme.colors.gallery.border.default};
  border-radius: 50%;
  opacity: 0.7;
`;

const EmptyStateTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
`;

const EmptyStateMessage = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const SearchContainer = styled.div<{ $galleryMode?: boolean }>`
  max-width: ${({ $galleryMode }) => $galleryMode ? '600px' : '500px'};
  margin: 0 auto ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.sm({ theme }) : theme.spacing.xl} auto;
`;

const SearchInput = styled.input<{ $galleryMode?: boolean }>`
  width: 100%;
  padding: ${({ theme, $galleryMode }) =>
    $galleryMode ? `${gallerySpacing.tiny({ theme })} ${gallerySpacing.sm({ theme })}` : theme.spacing.md};
  border: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `1px solid ${galleryColors.neutralLight({ theme })}`
      : `2px solid ${theme.colors.gray.light}`};
  border-radius: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryBorderRadius.comfortable({ theme }) : theme.borderRadius.lg};
  font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.metadata : theme.typography.fontSize.base};
  font-family: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontFamily.gallery.body : 'inherit'};
  background-color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.softWhite({ theme }) : 'white'};
  transition: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `all ${galleryTransitions.quick({ theme })}`
      : `border-color ${theme.transitions.fast}`};
  letter-spacing: ${({ $galleryMode }) => $galleryMode ? '0.005em' : 'normal'};

  &:focus {
    outline: none;
    border-color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.primary({ theme }) : theme.colors.primary};
    ${({ $galleryMode, theme }) => $galleryMode && `
      box-shadow: ${galleryShadows.focus({ theme })};
      background-color: ${galleryColors.white({ theme })};
    `}
  }

  &::placeholder {
    color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.neutralMedium({ theme }) : theme.colors.gray.medium};
  }
`;

const SearchStatus = styled.p`
  margin: ${({ theme }) => theme.spacing.xs} 0 0;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  text-align: center;
`;

const StatsContainer = styled.div<{ $galleryMode?: boolean }>`
  background: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.neutralLightest({ theme }) : theme.colors.gray.light};
  border-radius: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryBorderRadius.comfortable({ theme }) : theme.borderRadius.lg};
  padding: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.micro({ theme }) : theme.spacing.lg};
  margin-bottom: ${({ theme, $galleryMode }) =>
    $galleryMode ? gallerySpacing.sm({ theme }) : theme.spacing.xl};
  text-align: center;
  ${({ $galleryMode, theme }) => $galleryMode && `
    border: 1px solid ${galleryColors.neutralLight({ theme })};
  `}
`;

const StatsText = styled.p<{ $galleryMode?: boolean }>`
  color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.neutralDark({ theme }) : theme.colors.gray.dark};
  font-size: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontSize.gallery.metadata : theme.typography.fontSize.base};
  font-family: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontFamily.gallery.body : 'inherit'};
  font-weight: ${({ theme, $galleryMode }) =>
    $galleryMode ? theme.typography.fontWeight.gallery.light : 'normal'};
  letter-spacing: ${({ $galleryMode }) => $galleryMode ? '0.005em' : 'normal'};
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ActionButton = styled.button<{ $galleryMode?: boolean }>`
  display: block;
  min-height: 44px;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  padding: ${({ theme, $galleryMode }) =>
    $galleryMode
      ? `${gallerySpacing.xs({ theme })} ${gallerySpacing.sm({ theme })}`
      : `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.neutralLight({ theme }) : theme.colors.gray.medium};
  border-radius: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryBorderRadius.comfortable({ theme }) : theme.borderRadius.md};
  background: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.white({ theme }) : theme.colors.white};
  color: ${({ theme, $galleryMode }) =>
    $galleryMode ? galleryColors.charcoal({ theme }) : theme.colors.text};
  font: inherit;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme, $galleryMode }) =>
      $galleryMode ? galleryColors.primary({ theme }) : theme.colors.primary};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.semantic.focus};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

const NextPageError = styled.div`
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.gray.dark};
  text-align: center;
`;

function ArtistPreview({
  student,
}: {
  student: { latest_artworks?: Array<{ artwork_id: number; updated_at: string }> };
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const artwork = student.latest_artworks?.[0];

  if (!artwork || imageFailed) {
    return (
      <PreviewFrame data-preview-frame>
        <PreviewContent>
          <PreviewFallback>{artwork ? 'Preview unavailable' : 'No artworks yet'}</PreviewFallback>
        </PreviewContent>
      </PreviewFrame>
    );
  }

  return (
    <PreviewFrame data-preview-frame>
      <PreviewContent $galleryMode>
        <PreviewImage
          artworkId={artwork.artwork_id}
          variant="thumbnail"
          updatedAt={artwork.updated_at}
          alt=""
          onError={() => setImageFailed(true)}
          onLoadError={() => setImageFailed(true)}
        />
      </PreviewContent>
    </PreviewFrame>
  );
}

export interface ArtistsPageProps {
  galleryMode?: boolean;
}

interface ArtistsHistoryContext {
  queryString: string;
  scrollY: number;
  focusStudentId: number;
  page: number;
}

interface ArtistsLocationState {
  artistsContext?: ArtistsHistoryContext;
}

const ARTIST_PAGE_SIZE = 20;

export function ArtistsPage({ galleryMode = true }: ArtistsPageProps = {}) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const settledQuery = (searchParams.get('q') || '').trim();
  const [searchQuery, setSearchQuery] = useState(settledQuery);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoredHistoryEntry = useRef<string | null>(null);
  const historyContext = (location.state as ArtistsLocationState | null)?.artistsContext;

  const clearDebounce = () => {
    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
  };

  const commitQuery = useCallback((value: string) => {
    const nextQuery = value.trim();
    clearDebounce();
    if (nextQuery === settledQuery) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    if (nextQuery) {
      nextParams.set('q', nextQuery);
    } else {
      nextParams.delete('q');
    }
    setSearchParams(nextParams, { replace: false });
  }, [searchParams, setSearchParams, settledQuery]);

  // POP navigation must cancel an old debounce before the restored URL is
  // painted back into the input.
  useLayoutEffect(() => {
    clearDebounce();
    setSearchQuery(settledQuery);
  }, [location.key, settledQuery]);

  useEffect(() => {
    const nextQuery = searchQuery.trim();
    if (nextQuery === settledQuery) {
      return undefined;
    }

    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      commitQuery(nextQuery);
    }, 300);

    return clearDebounce;
  }, [commitQuery, searchQuery, settledQuery]);

  const studentsQuery = useInfiniteQuery({
    queryKey: ['visible-students', settledQuery],
    queryFn: ({ pageParam = 1, signal }) => studentService.getVisibleStudents(
      pageParam,
      ARTIST_PAGE_SIZE,
      settledQuery ? { search: settledQuery } : undefined,
      signal,
    ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 0,
    refetchOnMount: 'always',
    // Revalidate on mount, focus, reconnect, and browser Back/reentry before
    // rendering a previously cached directory snapshot.
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    networkMode: 'always',
    retry: false,
  });
  const {
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
    isFetchingNextPage,
  } = studentsQuery;
  const hasCachedDirectoryPages = Boolean(studentsQuery.data && studentsQuery.data.pages.length > 0);
  const directoryNotFetching = !studentsQuery.isFetching;

  const directoryVerified = Boolean(
    studentsQuery.isFetchedAfterMount &&
    studentsQuery.fetchStatus !== 'paused' &&
    !studentsQuery.isRefetching &&
    !studentsQuery.isRefetchError &&
    (
      studentsQuery.isSuccess ||
      (isFetchNextPageError && hasCachedDirectoryPages && directoryNotFetching)
    ),
  );
  const students = directoryVerified
    ? studentsQuery.data?.pages.flatMap((page) => page.students) || []
    : [];
  const totalStudents = directoryVerified ? studentsQuery.data?.pages[0]?.total || 0 : 0;
  const pageTarget = historyContext?.page || 1;
  const hasMatchingHistoryContext = !historyContext || historyContext.queryString === searchParams.toString();
  const pageTargetReady = !hasMatchingHistoryContext || (studentsQuery.data?.pages.length || 0) >= pageTarget;

  useEffect(() => {
    if (
      !directoryVerified ||
      !historyContext ||
      historyContext.queryString !== searchParams.toString() ||
      pageTargetReady ||
      !hasNextPage ||
      isFetchingNextPage ||
      isFetchNextPageError
    ) {
      return;
    }
    void fetchNextPage();
  }, [directoryVerified, fetchNextPage, hasNextPage, historyContext, isFetchNextPageError, isFetchingNextPage, pageTargetReady, searchParams]);

  useEffect(() => {
    if (
      !directoryVerified ||
      !pageTargetReady ||
      !historyContext ||
      historyContext.queryString !== searchParams.toString() ||
      restoredHistoryEntry.current === location.key
    ) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: historyContext.scrollY, behavior: 'auto' });
      document.querySelector<HTMLElement>(`[data-student-id="${historyContext.focusStudentId}"]`)?.focus({ preventScroll: true });
      restoredHistoryEntry.current = location.key;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [directoryVerified, historyContext, location.key, pageTargetReady, searchParams]);

  const rememberBrowsingEntry = (studentId: number) => {
    const currentHistoryState = window.history.state;
    const currentUserState = currentHistoryState && typeof currentHistoryState.usr === 'object'
      ? currentHistoryState.usr
      : {};
    const loadedPage = students.findIndex((student) => student.student_id === studentId);
    window.history.replaceState(
      {
        ...currentHistoryState,
        usr: {
          ...currentUserState,
          artistsContext: {
            queryString: searchParams.toString(),
            scrollY: window.scrollY,
            focusStudentId: studentId,
            page: Math.max(1, Math.floor(Math.max(loadedPage, 0) / ARTIST_PAGE_SIZE) + 1),
          } satisfies ArtistsHistoryContext,
        },
      },
      '',
      window.location.href,
    );
  };

  const apiError = studentsQuery.error as any;
  const isSettlingQuery = searchQuery.trim() !== settledQuery;
  const showLoading = !directoryVerified && (studentsQuery.isPending || studentsQuery.isFetching || studentsQuery.fetchStatus === 'paused');
  const showError = studentsQuery.isError && !isFetchNextPageError && !studentsQuery.isFetching && studentsQuery.fetchStatus !== 'paused';

  return (
    <ErrorBoundary>
      <PageContainer $galleryMode={galleryMode}>
        <Header $galleryMode={galleryMode}>
          <Title $galleryMode={galleryMode}>Artists</Title>
          <Subtitle $galleryMode={galleryMode}>
            Discover the student artists in our community and explore their work.
          </Subtitle>
        </Header>

        <SearchContainer $galleryMode={galleryMode}>
          <SearchInput
            ref={searchInputRef}
            $galleryMode={galleryMode}
            type="search"
            aria-label="Search artists by name"
            placeholder="Search artists by name..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitQuery(searchQuery);
              }
            }}
            />
          {isSettlingQuery && (
            <SearchStatus role="status">Updating artist results…</SearchStatus>
          )}
        </SearchContainer>

        {directoryVerified && (
          <StatsContainer $galleryMode={galleryMode}>
            <StatsText $galleryMode={galleryMode}>
              Showing {students.length} of {totalStudents} artists
            </StatsText>
          </StatsContainer>
        )}

        {showLoading ? (
          <LoadingContainer>
            <LoadingSpinner text="Loading artists..." />
          </LoadingContainer>
        ) : showError ? (
          apiError?.type === 'service_outage' || apiError?.type === 'network' ? (
            <ServiceOutageIndicator
              error={apiError}
              variant="card"
              onRetry={async () => { await studentsQuery.refetch(); }}
              onCheckStatus={studentsQuery.refetch}
            />
          ) : apiError?.isRetryable ? (
            <RetryableError
              error={apiError}
              onRetry={async () => {
                const result = await studentsQuery.refetch();
                if (result.isError) {
                  throw result.error;
                }
              }}
              variant="full"
              retryConfig={{ maxAttempts: 3, initialDelay: 2000, enableAutoRetry: false }}
            />
          ) : (
            <>
              <InlineError
                error={apiError || new Error('Failed to load artists')}
                title="Failed to Load Artists"
              />
              <ActionButton
                $galleryMode={galleryMode}
                type="button"
                onClick={() => void studentsQuery.refetch()}
              >
                Retry loading artists
              </ActionButton>
            </>
          )
        ) : directoryVerified && students.length === 0 ? (
          <EmptyState>
              <EmptyStateIcon aria-hidden="true" />
            <EmptyStateTitle>
              {settledQuery ? 'No Artists Match Your Search' : 'No Artists Available'}
            </EmptyStateTitle>
            <EmptyStateMessage>
              {settledQuery
                ? `No artists found matching “${settledQuery}”. Try a different search term.`
                : 'There are currently no visible student artists in the gallery.'}
            </EmptyStateMessage>
            {settledQuery && (
              <ActionButton
                $galleryMode={galleryMode}
                type="button"
                onClick={() => {
                  commitQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                Clear search
              </ActionButton>
            )}
          </EmptyState>
        ) : directoryVerified ? (
          <GalleryGrid
            galleryMode={true}
            animate={false}
          >
            {students.map((student) => (
              <StudentCard
                key={student.student_id}
                to={`/artists/${student.student_id}`}
                state={{ fromArtistsDirectory: true }}
                data-student-id={student.student_id}
                $galleryMode={galleryMode}
                onClick={() => rememberBrowsingEntry(student.student_id)}
              >
                <ArtistPreview student={student} />
                <StudentHeader $galleryMode={galleryMode}>
                  <StudentName $galleryMode={galleryMode}>{student.name}</StudentName>
                  <ArtworkCount $galleryMode={galleryMode}>
                    {student.artwork_count || 0} {student.artwork_count === 1 ? 'work' : 'works'}
                  </ArtworkCount>
                </StudentHeader>
              </StudentCard>
            ))}
          </GalleryGrid>
        ) : null}

        {directoryVerified && isFetchNextPageError && (
          <NextPageError role="alert">
            More artists are unavailable right now.
            <ActionButton
              $galleryMode={galleryMode}
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              Retry loading artists
            </ActionButton>
          </NextPageError>
        )}

        {directoryVerified && hasNextPage && !isFetchNextPageError && (
          <ActionButton
            $galleryMode={galleryMode}
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage
              ? 'Loading more artists…'
              : isFetchNextPageError
                ? 'Retry loading artists'
                : 'Load more artists'}
          </ActionButton>
        )}
      </PageContainer>
    </ErrorBoundary>
  );
}
